import { Organization, PipelineStage } from '../types'
import { supabase } from './supabaseClient'
import { organizationPatch, stagePatch, toOrganization } from './mappers'
import { getDb, refresh, requireOrganizationId } from './store'

export function getOrganization(organizationId: string): Organization | undefined {
  return getDb().organizations.find((o) => o.id === organizationId)
}

export async function updateOrganization(
  organizationId: string,
  patch: Partial<Organization>,
): Promise<void> {
  const current = getOrganization(organizationId)
  const merged: Partial<Organization> = {
    ...patch,
    brand: patch.brand ? { ...current?.brand, ...patch.brand } : undefined,
  }
  const { error } = await supabase
    .from('organizations')
    .update(organizationPatch(merged))
    .eq('id', organizationId)
  if (error) throw new Error(error.message)
  await refresh()
}

/** Marca de la empresa para las páginas públicas (visitante anónimo). */
export async function fetchPublicOrganization(
  organizationId: string,
): Promise<Organization | null> {
  const { data } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', organizationId)
    .maybeSingle()
  return data ? toOrganization(data) : null
}

export function listStages(organizationId: string): PipelineStage[] {
  return getDb()
    .stages.filter((s) => s.organizationId === organizationId)
    .sort((a, b) => a.order - b.order)
}

export async function createStage(
  organizationId: string,
  name: string,
  color: string,
): Promise<void> {
  requireOrganizationId(organizationId, 'crear el estado')
  const position = listStages(organizationId).length + 1
  const { error } = await supabase.from('pipeline_stages').insert({
    organization_id: organizationId,
    name,
    color,
    position,
    is_active: true,
    is_initial: position === 1,
  })
  if (error) throw new Error(error.message)
  await refresh()
}

export async function updateStage(
  stageId: string,
  patch: Partial<PipelineStage>,
): Promise<void> {
  const { error } = await supabase
    .from('pipeline_stages')
    .update(stagePatch(patch))
    .eq('id', stageId)
  if (error) throw new Error(error.message)
  await refresh()
}

/**
 * Borrado lógico del estado. Los leads que estuvieran en él pasan al estado
 * inicial para no perder ningún contacto.
 */
export async function deleteStage(stageId: string): Promise<void> {
  const db = getDb()
  const stage = db.stages.find((s) => s.id === stageId)
  if (!stage) return
  const fallback =
    db.stages.find(
      (s) =>
        s.organizationId === stage.organizationId && s.isInitial && s.id !== stageId,
    ) ??
    db.stages.find(
      (s) => s.organizationId === stage.organizationId && s.id !== stageId,
    )
  if (!fallback) return

  await supabase.from('leads').update({ stage_id: fallback.id }).eq('stage_id', stageId)
  const { error } = await supabase
    .from('pipeline_stages')
    .update({ deleted_at: new Date().toISOString(), is_active: false })
    .eq('id', stageId)
  if (error) throw new Error(error.message)
  await refresh()
}

export async function setInitialStage(
  organizationId: string,
  stageId: string,
): Promise<void> {
  await supabase
    .from('pipeline_stages')
    .update({ is_initial: false })
    .eq('organization_id', organizationId)
  const { error } = await supabase
    .from('pipeline_stages')
    .update({ is_initial: true })
    .eq('id', stageId)
  if (error) throw new Error(error.message)
  await refresh()
}

export async function reorderStages(
  organizationId: string,
  orderedIds: string[],
): Promise<void> {
  await Promise.all(
    orderedIds.map((id, index) =>
      supabase
        .from('pipeline_stages')
        .update({ position: index + 1 })
        .eq('id', id)
        .eq('organization_id', organizationId),
    ),
  )
  await refresh()
}
